<template>
  <!--
    Scoring-tier editor. v-model is the tier array:
    [{ label, score, min_rate, max_rate }] with rates stored 0–1 and shown as %.

    Used by both QualityMetricSetup modals (add assignment, edit tiers), which
    previously carried two copies of this table.
  -->
  <div>
    <table v-if="rows.length" class="w-full text-xs mb-2">
      <thead>
        <tr class="border-b border-gray-200">
          <th class="px-2 py-1 text-left text-[11px] font-semibold text-gray-600">Label</th>
          <th class="px-2 py-1 text-right text-[11px] font-semibold text-gray-600 w-16">Score</th>
          <th class="px-2 py-1 text-right text-[11px] font-semibold text-gray-600 w-24">Min %</th>
          <th class="px-2 py-1 text-right text-[11px] font-semibold text-gray-600 w-24">Max %</th>
          <th class="px-2 py-1 w-8"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(tier, i) in rows" :key="i" class="border-b border-gray-100">
          <td class="px-2 py-1">
            <input
              v-model="tier.label"
              type="text"
              placeholder="e.g. 4 Star"
              class="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-400"
              @input="emit"
            />
          </td>
          <td class="px-2 py-1">
            <input
              v-model.number="tier.score"
              type="number" step="1" min="0"
              class="w-14 border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-blue-400"
              @input="emit"
            />
          </td>
          <td class="px-2 py-1">
            <input
              v-model.number="tier.min_pct"
              type="number" step="0.01" min="0" max="100" placeholder="—"
              class="w-full border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-blue-400"
              @input="emit"
            />
          </td>
          <td class="px-2 py-1">
            <input
              v-model.number="tier.max_pct"
              type="number" step="0.01" min="0" max="100" placeholder="—"
              class="w-full border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-blue-400"
              @input="emit"
            />
          </td>
          <td class="px-2 py-1 text-center">
            <button class="text-gray-300 hover:text-red-500" @click="removeRow(i)">&times;</button>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-else class="text-xs text-gray-400 mb-2">No tiers configured.</p>

    <button class="text-xs font-medium text-gray-600 hover:text-gray-900" @click="addRow">
      + Add tier
    </button>
  </div>
</template>

<script>
export default {
  name: 'TierTable',
  props: {
    modelValue: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue'],
  data() {
    return { rows: [] };
  },
  watch: {
    modelValue: { immediate: true, handler() { this.hydrate(); } },
  },
  methods: {
    // Rates are persisted 0–1 but edited as percentages. Real tiers carry 4
    // decimals (0.4233 = 42.33%), so round-tripping must not lose precision:
    // toFixed(1) would silently rewrite 0.4233 as 0.423 on any unrelated edit.
    // Use enough decimals to represent the stored value exactly, and clean the
    // float noise that *100 and /100 introduce.
    hydrate() {
      const incoming = this.modelValue || [];
      this.rows = incoming.map((t) => ({
        label: t.label || '',
        score: t.score ?? 0,
        min_pct: this.toPct(t.min_rate),
        max_pct: this.toPct(t.max_rate),
      }));
    },
    toPct(rate) {
      return rate == null ? null : +(rate * 100).toPrecision(12);
    },
    toRate(pct) {
      return pct == null || pct === '' ? null : +(pct / 100).toPrecision(12);
    },
    emit() {
      this.$emit('update:modelValue', this.rows.map((r) => {
        const tier = { label: r.label, score: r.score || 0 };
        const min = this.toRate(r.min_pct);
        const max = this.toRate(r.max_pct);
        if (min != null) tier.min_rate = min;
        if (max != null) tier.max_rate = max;
        return tier;
      }));
    },
    addRow() {
      this.rows.push({ label: '', score: 0, min_pct: null, max_pct: null });
      this.emit();
    },
    removeRow(i) {
      this.rows.splice(i, 1);
      this.emit();
    },
  },
};
</script>
